import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Calendar, Clock, LogOut, Plus, CheckCircle2, XCircle, RefreshCw,
  User as UserIcon, ChevronDown, Loader2, ArrowRight,
} from 'lucide-react';
import patientApi, { clearPatientSession, getPatientPhone, isPatientAuthed } from '../utils/patientApi';

const STATUS_STYLES = {
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  confirmed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rescheduled: 'bg-amber-100 text-amber-700 border-amber-200',
  forwarded: 'bg-slate-100 text-slate-600 border-slate-200',
  checked_in: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  checked_out: 'bg-slate-200 text-slate-700 border-slate-300',
  cancelled: 'bg-rose-100 text-rose-700 border-rose-200',
  no_show: 'bg-orange-100 text-orange-700 border-orange-200',
};

const CANCEL_REASONS = [
  { value: 'cant_make_it', label: "Can't make it" },
  { value: 'found_another', label: 'Found another clinic' },
  { value: 'feeling_better', label: 'Feeling better' },
  { value: 'financial', label: 'Financial reasons' },
  { value: 'other', label: 'Other' },
];

function formatDateLabel(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

export default function PatientPortalDashboardPage() {
  const navigate = useNavigate();
  const phone = getPatientPhone();
  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionId, setActionId] = useState('');

  // Booking modal state
  const [bookOpen, setBookOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [bookServiceId, setBookServiceId] = useState('');
  const [slots, setSlots] = useState([]);
  const [bookDate, setBookDate] = useState('');
  const [bookTime, setBookTime] = useState('');
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Reschedule modal state
  const [reschedTarget, setReschedTarget] = useState(null);
  const [reschedSlots, setReschedSlots] = useState([]);
  const [reschedDate, setReschedDate] = useState('');
  const [reschedTime, setReschedTime] = useState('');
  const [reschedLoading, setReschedLoading] = useState(false);

  // Cancel modal state
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('other');
  const [cancelNotes, setCancelNotes] = useState('');

  useEffect(() => {
    if (!isPatientAuthed()) {
      navigate('/patient-portal');
      return;
    }
    (async () => {
      try {
        const res = await patientApi.get('/patient-portal/me/profiles');
        setProfiles(res.data.profiles || []);
        if (res.data.profiles?.length) {
          setActiveProfileId(res.data.profiles[0].id);
        }
      } catch (err) {
        toast.error('Could not load your profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const loadAppointments = useCallback(async () => {
    if (!activeProfileId) return;
    setRefreshing(true);
    try {
      const res = await patientApi.get('/patient-portal/me/appointments', {
        params: { patient_id: activeProfileId },
      });
      setAppointments(res.data || []);
    } catch (err) {
      toast.error('Could not load your appointments');
    } finally {
      setRefreshing(false);
    }
  }, [activeProfileId]);

  useEffect(() => {
    if (activeProfileId) loadAppointments();
  }, [activeProfileId, loadAppointments]);

  const activeProfile = useMemo(
    () => profiles.find((p) => p.id === activeProfileId),
    [profiles, activeProfileId],
  );

  const { upcoming, past } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const up = [];
    const pa = [];
    for (const a of appointments) {
      const isPast = (a.scheduled_date || '') < today || ['cancelled', 'checked_out', 'no_show', 'forwarded'].includes(a.status);
      if (isPast) pa.push(a); else up.push(a);
    }
    up.sort((a, b) => (a.scheduled_date + (a.scheduled_time || '')).localeCompare(b.scheduled_date + (b.scheduled_time || '')));
    pa.sort((a, b) => (b.scheduled_date + (b.scheduled_time || '')).localeCompare(a.scheduled_date + (a.scheduled_time || '')));
    return { upcoming: up, past: pa };
  }, [appointments]);

  const handleLogout = () => {
    clearPatientSession();
    navigate('/patient-portal');
  };

  const handleConfirm = async (apt) => {
    setActionId(apt.id);
    try {
      await patientApi.post(`/patient-portal/me/appointments/${apt.id}/confirm`);
      toast.success('Appointment confirmed');
      await loadAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not confirm');
    } finally {
      setActionId('');
    }
  };

  // --- Booking flow ---
  const openBookModal = async () => {
    setBookOpen(true);
    setBookServiceId('');
    setBookDate('');
    setBookTime('');
    setSlots([]);
    try {
      const res = await patientApi.get('/patient-portal/services');
      setServices(res.data || []);
    } catch (err) {
      toast.error('Could not load services');
    }
  };

  useEffect(() => {
    if (!bookServiceId) return;
    setBookDate('');
    setBookTime('');
    setSlotsLoading(true);
    patientApi.get('/patient-portal/available-slots', { params: { service_id: bookServiceId } })
      .then((res) => setSlots(res.data.available_slots || []))
      .catch(() => toast.error('Could not load slots'))
      .finally(() => setSlotsLoading(false));
  }, [bookServiceId]);

  const bookServiceMeta = services.find((s) => s.id === bookServiceId);
  const bookDayMeta = slots.find((s) => s.date === bookDate);

  const handleBook = async () => {
    if (!bookServiceId || !bookDate) {
      toast.error('Pick a service and date');
      return;
    }
    if (bookServiceMeta?.type === 'time_based' && !bookTime) {
      toast.error('Pick a time slot');
      return;
    }
    try {
      await patientApi.post('/patient-portal/me/appointments', {
        patient_id: activeProfileId,
        service_id: bookServiceId,
        scheduled_date: bookDate,
        scheduled_time: bookServiceMeta?.type === 'time_based' ? bookTime : null,
      });
      toast.success('Appointment confirmed!');
      setBookOpen(false);
      await loadAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not book');
    }
  };

  // --- Reschedule flow ---
  const openReschedule = async (apt) => {
    setReschedTarget(apt);
    setReschedDate('');
    setReschedTime('');
    setReschedSlots([]);
    setReschedLoading(true);
    try {
      const res = await patientApi.get('/patient-portal/available-slots', { params: { service_id: apt.service_id } });
      setReschedSlots(res.data.available_slots || []);
    } catch {
      toast.error('Could not load slots');
    } finally {
      setReschedLoading(false);
    }
  };

  const reschedDayMeta = reschedSlots.find((s) => s.date === reschedDate);

  const handleReschedule = async () => {
    if (!reschedDate) { toast.error('Pick a date'); return; }
    if (reschedTarget.service_type === 'time_based' && !reschedTime) {
      toast.error('Pick a time'); return;
    }
    try {
      await patientApi.post(`/patient-portal/me/appointments/${reschedTarget.id}/reschedule`, {
        new_date: reschedDate,
        new_time: reschedTarget.service_type === 'time_based' ? reschedTime : null,
      });
      toast.success('Appointment rescheduled');
      setReschedTarget(null);
      await loadAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reschedule failed');
    }
  };

  // --- Cancel flow ---
  const handleCancel = async () => {
    try {
      await patientApi.post(`/patient-portal/me/appointments/${cancelTarget.id}/cancel`, {
        reason_category: cancelReason,
        notes: cancelNotes,
      });
      toast.success('Appointment cancelled');
      setCancelTarget(null);
      setCancelNotes('');
      setCancelReason('other');
      await loadAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold">P</div>
            <div>
              <p className="text-sm text-slate-500 leading-none">Signed in as</p>
              <p className="font-semibold text-slate-900" data-testid="patient-portal-phone">+91 {phone}</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout} className="rounded-xl" data-testid="patient-portal-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Profile picker */}
        {profiles.length > 0 && (
          <Card className="p-4 rounded-2xl border-slate-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <UserIcon className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-xs text-slate-500">Viewing profile</p>
                  <p className="font-semibold text-slate-900" data-testid="patient-portal-active-profile">
                    {activeProfile?.name || '—'}
                  </p>
                </div>
              </div>
              {profiles.length > 1 ? (
                <div className="flex items-center gap-2" data-testid="patient-portal-profile-picker">
                  <Label className="text-xs text-slate-500">Switch:</Label>
                  <Select value={activeProfileId} onValueChange={setActiveProfileId}>
                    <SelectTrigger className="w-56 rounded-xl" data-testid="patient-portal-profile-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {profiles.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`patient-portal-profile-option-${p.id}`}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <Badge variant="outline" className="text-xs">Single profile</Badge>
              )}
            </div>
          </Card>
        )}

        {/* Quick actions */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-slate-900">Your appointments</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={loadAppointments} disabled={refreshing} className="rounded-xl" data-testid="patient-portal-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button onClick={openBookModal} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="patient-portal-book-new-btn">
              <Plus className="w-4 h-4 mr-2" /> Book new
            </Button>
          </div>
        </div>

        {/* Upcoming */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Upcoming</h3>
          {upcoming.length === 0 ? (
            <Card className="p-8 text-center rounded-2xl border-dashed border-slate-300 bg-white/50">
              <p className="text-slate-500">No upcoming appointments.</p>
              <Button onClick={openBookModal} className="mt-3 bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="patient-portal-empty-book-btn">
                Book your first one <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Card>
          ) : upcoming.map((apt) => (
            <Card key={apt.id} className="p-5 rounded-2xl border-slate-200" data-testid={`patient-portal-upcoming-appointment-${apt.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-slate-900">{apt.service_name}</h4>
                    <Badge className={`text-xs border ${STATUS_STYLES[apt.status] || 'bg-slate-100 text-slate-700'}`}>{apt.status}</Badge>
                    {apt.ref_number && <span className="text-xs text-slate-400">{apt.ref_number}</span>}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {formatDateLabel(apt.scheduled_date)}</span>
                    {apt.scheduled_time && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {apt.scheduled_time}</span>}
                  </div>
                  {(apt.created_by_user_name || apt.last_updated_by_user_name) && (
                    <div className="text-[11px] text-slate-400 pt-1">
                      {apt.last_updated_by_user_name
                        ? `Last updated by: ${apt.last_updated_by_user_name.split(' ')[0]}`
                        : `Created by: ${apt.created_by_user_name.split(' ')[0]}`}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {apt.status !== 'confirmed' && (
                    <Button size="sm" onClick={() => handleConfirm(apt)} disabled={actionId === apt.id} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid={`patient-portal-confirm-btn-${apt.id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Confirm
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openReschedule(apt)} className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`patient-portal-reschedule-btn-${apt.id}`}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Reschedule
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setCancelTarget(apt)} className="rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`patient-portal-cancel-btn-${apt.id}`}>
                    <XCircle className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </section>

        {/* Past */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">History</h3>
          {past.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No past appointments yet.</p>
          ) : past.map((apt) => (
            <Card key={apt.id} className="p-4 rounded-2xl border-slate-200 bg-white/70" data-testid={`patient-portal-history-appointment-${apt.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium text-slate-800">{apt.service_name}</h4>
                    <Badge className={`text-xs border ${STATUS_STYLES[apt.status] || 'bg-slate-100 text-slate-700'}`}>{apt.status}</Badge>
                  </div>
                  <div className="text-sm text-slate-500">
                    {formatDateLabel(apt.scheduled_date)}{apt.scheduled_time ? ` · ${apt.scheduled_time}` : ''}
                  </div>
                </div>
                {apt.remarks && <p className="text-xs text-slate-400 max-w-xs truncate">{apt.remarks}</p>}
              </div>
            </Card>
          ))}
        </section>
      </main>

      {/* Book Modal */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="rounded-2xl" data-testid="patient-portal-book-dialog">
          <DialogHeader>
            <DialogTitle>Book a new appointment</DialogTitle>
            <DialogDescription>Pick a service, then choose an available date and time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Service</Label>
              <Select value={bookServiceId} onValueChange={setBookServiceId}>
                <SelectTrigger className="rounded-xl" data-testid="patient-portal-book-service-select">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {bookServiceId && (
              <div>
                <Label className="text-sm">Date</Label>
                {slotsLoading ? (
                  <div className="text-sm text-slate-500 py-2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading slots…</div>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-slate-500 py-2">No slots available in the next 30 days.</p>
                ) : (
                  <Select value={bookDate} onValueChange={(v) => { setBookDate(v); setBookTime(''); }}>
                    <SelectTrigger className="rounded-xl" data-testid="patient-portal-book-date-select">
                      <SelectValue placeholder="Choose a date" />
                    </SelectTrigger>
                    <SelectContent>
                      {slots.map((s) => (
                        <SelectItem key={s.date} value={s.date}>{s.formatted_date} ({s.day_name})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {bookDate && bookServiceMeta?.type === 'time_based' && bookDayMeta && (
              <div>
                <Label className="text-sm">Time</Label>
                <div className="flex flex-wrap gap-2 mt-1" data-testid="patient-portal-book-time-grid">
                  {bookDayMeta.slots.map((t) => (
                    <button
                      key={t}
                      onClick={() => setBookTime(t)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${bookTime === t ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400'}`}
                      data-testid={`patient-portal-book-time-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleBook} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="patient-portal-book-confirm-btn">
              Confirm booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog open={!!reschedTarget} onOpenChange={(open) => !open && setReschedTarget(null)}>
        <DialogContent className="rounded-2xl" data-testid="patient-portal-reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule {reschedTarget?.service_name}</DialogTitle>
            <DialogDescription>Choose a new date and time. Your old appointment will be replaced.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Currently: {reschedTarget && formatDateLabel(reschedTarget.scheduled_date)}
              {reschedTarget?.scheduled_time ? ` · ${reschedTarget.scheduled_time}` : ''}
            </p>
            <div>
              <Label className="text-sm">New date</Label>
              {reschedLoading ? (
                <div className="text-sm text-slate-500 py-2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : reschedSlots.length === 0 ? (
                <p className="text-sm text-slate-500 py-2">No available dates in the next 30 days.</p>
              ) : (
                <Select value={reschedDate} onValueChange={(v) => { setReschedDate(v); setReschedTime(''); }}>
                  <SelectTrigger className="rounded-xl" data-testid="patient-portal-resched-date-select">
                    <SelectValue placeholder="Choose a date" />
                  </SelectTrigger>
                  <SelectContent>
                    {reschedSlots.map((s) => (
                      <SelectItem key={s.date} value={s.date}>{s.formatted_date} ({s.day_name})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {reschedDate && reschedTarget?.service_type === 'time_based' && reschedDayMeta && (
              <div>
                <Label className="text-sm">Time</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {reschedDayMeta.slots.map((t) => (
                    <button
                      key={t}
                      onClick={() => setReschedTime(t)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${reschedTime === t ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
                      data-testid={`patient-portal-resched-time-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedTarget(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleReschedule} className="bg-amber-500 hover:bg-amber-600 rounded-xl text-white" data-testid="patient-portal-resched-confirm-btn">
              Confirm reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent className="rounded-2xl" data-testid="patient-portal-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel appointment</DialogTitle>
            <DialogDescription>Let us know why you're cancelling so we can serve you better.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              You're cancelling <strong>{cancelTarget?.service_name}</strong> on {cancelTarget && formatDateLabel(cancelTarget.scheduled_date)}.
            </p>
            <div>
              <Label className="text-sm">Reason</Label>
              <Select value={cancelReason} onValueChange={setCancelReason}>
                <SelectTrigger className="rounded-xl" data-testid="patient-portal-cancel-reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CANCEL_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Textarea
                value={cancelNotes}
                onChange={(e) => setCancelNotes(e.target.value)}
                placeholder="Anything we should know?"
                className="rounded-xl"
                data-testid="patient-portal-cancel-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)} className="rounded-xl">Keep appointment</Button>
            <Button onClick={handleCancel} className="bg-rose-600 hover:bg-rose-700 rounded-xl text-white" data-testid="patient-portal-cancel-confirm-btn">
              Cancel appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
